/**
 * 数字雨物理模块 - 名字粒子与色块粒子独立飘动 + 碰边弹回
 */

export interface DriftOptions {
  /** 名字粒子数 */
  nameCount?: number;
  /** 色块粒子数 */
  blockCount?: number;
  /** 速度 px/s */
  speed?: number;
  /** 名字池 */
  namePool?: string[];
  /** 色块调色板 */
  colors?: string[];
  /** 字体大小（名字） */
  fontSize?: number;
}

const DEFAULT_NAMES = [
  "王", "李", "张", "小明", "阿伟", "小张", "林", "陈", "苏", "周",
  "张三", "李四", "王五", "赵六", "阿牛", "小杰", "小满", "婉清"
];

const DEFAULT_COLORS = [
  "#ff5577", "#5cd4ff", "#88dd66", "#ffcc44", "#cc88ff",
  "#ff8855", "#66ddaa", "#ff66aa", "#44bbff", "#ddaa55",
  "#aaaaff", "#ff7788", "#ff9966", "#77ddaa"
];

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function randDir(): number {
  return Math.random() < 0.5 ? -1 : 1;
}

interface BaseParticle {
  el: HTMLElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
}

interface NameParticle extends BaseParticle {
  text: string;
}

interface BlockParticle extends BaseParticle {
  variant: "short" | "normal" | "long";
}

export interface DriftController {
  destroy: () => void;
  setNames: (names: string[]) => void;
  appendName: (name: string) => void;
}

export function createDrift(
  container: HTMLElement,
  fontFamily: string,
  options: DriftOptions = {}
): DriftController {
  const nameCount = options.nameCount ?? 12;
  const blockCount = options.blockCount ?? 18;
  const speed = options.speed ?? 60;
  const namePool = options.namePool ?? DEFAULT_NAMES;
  const colors = options.colors ?? DEFAULT_COLORS;
  const fontSize = options.fontSize ?? 14;

  const layer = document.createElement("div");
  layer.className = "drift";
  layer.style.cssText = "position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:2;";
  container.appendChild(layer);

  let W = container.clientWidth || 720;
  let H = container.clientHeight || 540;

  const onResize = () => {
    W = container.clientWidth || 720;
    H = container.clientHeight || 540;
  };
  window.addEventListener("resize", onResize);

  // 名字粒子
  const nameParticles: NameParticle[] = [];
  for (let i = 0; i < nameCount; i++) {
    const p = document.createElement("div");
    p.className = "name-p";
    p.style.cssText = `
      position:absolute;
      font-family: ${fontFamily};
      font-size: ${fontSize}px;
      line-height: 1;
      white-space: nowrap;
      user-select: none;
      pointer-events: none;
    `;
    const name = namePool[Math.floor(Math.random() * namePool.length)];
    p.textContent = name;
    p.style.color = colors[Math.floor(Math.random() * colors.length)];
    p.style.opacity = (0.7 + Math.random() * 0.25).toFixed(2);
    layer.appendChild(p);

    const w = p.offsetWidth || name.length * fontSize;
    const h = p.offsetHeight || fontSize + 2;
    nameParticles.push({
      el: p,
      x: rand(0, Math.max(1, W - w)),
      y: rand(0, Math.max(1, H - h)),
      vx: randDir() * rand(0.5, 1) * speed,
      vy: randDir() * rand(0.5, 1) * speed,
      w,
      h,
      text: name,
    });
  }

  // 色块粒子
  const blockParticles: BlockParticle[] = [];
  for (let i = 0; i < blockCount; i++) {
    const p = document.createElement("div");
    p.className = "blk-p";
    const r = Math.random();
    const variant: BlockParticle["variant"] = r < 0.25 ? "short" : r > 0.75 ? "long" : "normal";
    const h = variant === "short" ? 9 : variant === "long" ? 24 : 18;
    p.style.cssText = `
      position:absolute;
      width: 9px;
      height: ${h}px;
      background: ${colors[Math.floor(Math.random() * colors.length)]};
      opacity: ${(0.6 + Math.random() * 0.3).toFixed(2)};
    `;
    layer.appendChild(p);

    blockParticles.push({
      el: p,
      x: rand(0, Math.max(1, W - 9)),
      y: rand(0, Math.max(1, H - h)),
      vx: randDir() * rand(0.5, 1) * speed,
      vy: randDir() * rand(0.5, 1) * speed,
      w: 9,
      h,
      variant,
    });
  }

  // 物理循环
  let lastT = performance.now();
  let raf = 0;
  let running = true;
  function loop(t: number) {
    if (!running) return;
    const dt = Math.min(0.05, (t - lastT) / 1000);
    lastT = t;

    const all: BaseParticle[] = (nameParticles as unknown as BaseParticle[]).concat(blockParticles as unknown as BaseParticle[]);
    for (let i = 0; i < all.length; i++) {
      const p = all[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.x < 0) { p.x = 0; p.vx = -p.vx; }
      if (p.x + p.w > W) { p.x = W - p.w; p.vx = -p.vx; }
      if (p.y < 0) { p.y = 0; p.vy = -p.vy; }
      if (p.y + p.h > H) { p.y = H - p.h; p.vy = -p.vy; }
      p.el.style.transform = `translate(${p.x}px, ${p.y}px)`;
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);

  // 对外接口
  const setNames = (names: string[]) => {
    for (const p of nameParticles) {
      if (names.length > 0) {
        const n = names[Math.floor(Math.random() * names.length)];
        p.text = n;
        p.el.textContent = n;
        const w = p.el.offsetWidth || n.length * fontSize;
        p.w = w;
        p.x = rand(0, Math.max(1, W - w));
      }
    }
  };

  const appendName = (name: string) => {
    // 替换一个粒子的文本
    if (nameParticles.length === 0) return;
    const p = nameParticles[Math.floor(Math.random() * nameParticles.length)];
    p.text = name;
    p.el.textContent = name;
    const w = p.el.offsetWidth || name.length * fontSize;
    p.w = w;
    if (p.x + w > W) p.x = Math.max(0, W - w);
  };

  const destroy = () => {
    running = false;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", onResize);
    layer.remove();
  };

  return { destroy, setNames, appendName };
}
